import { toUtcIsoStringFromCompanyLocal } from '@/utils/displayContext';

export const HR_SEGMENT_REGULAR = 'REGULAR';
export const HR_SEGMENT_OVERTIME_BEFORE = 'OVERTIME_BEFORE_SHIFT';
export const HR_SEGMENT_OVERTIME_AFTER = 'OVERTIME_AFTER_SHIFT';
export const HR_SEGMENT_OVERTIME_UNSCHEDULED = 'OVERTIME_UNSCHEDULED';

const MINUTES_PER_DAY = 24 * 60;

export type HrOvertimeSplitSegment = {
  type: string;
  overtime: boolean;
  /** UTC ISO instants, ready for the shared clock formatters. */
  start: string;
  end: string;
  minutes: number;
};

export type HrOvertimeSplit = {
  workedMinutes: number;
  breakMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;
  segments: HrOvertimeSplitSegment[];
};

export type HrOvertimeSplitInput = {
  /** Company-local picker values, e.g. "2026-07-15T12:00". */
  checkIn: string;
  checkOut: string;
  expectedMinutes: number;
  breakMinutes?: number;
  /** Scheduled shift window as company-local clock times, e.g. "13:00:00". */
  shiftStartTime?: string | null;
  shiftEndTime?: string | null;
  /**
   * Regular (in-shift) minutes already recorded on other punches of the same calendar day.
   * Remaining expected for this punch becomes {@code expected − other}, matching backend
   * day allocation which subtracts regular minutes (not productive/OT).
   */
  otherDayRegularMinutes?: number;
  /**
   * Unpaid break minutes already applied on earlier punches of the same day. Remaining break
   * for this punch becomes {@code breakMinutes − other}, matching backend day allocation.
   */
  otherDayBreakMinutes?: number;
};

const EMPTY_SPLIT: HrOvertimeSplit = {
  workedMinutes: 0,
  breakMinutes: 0,
  regularMinutes: 0,
  overtimeMinutes: 0,
  segments: [],
};

function minuteOfDay(value?: string | null): number | null {
  if (!value) return null;
  const match = /(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Mirrors the backend split of a punch against the scheduled shift window: hours inside the shift
 * are regular, hours before and after it are overtime candidates. Payable overtime is then capped
 * to excess over expected minutes so early-in / early-out that still meets the day does not create OT.
 * Used to preview and validate overtime before the punch is saved.
 */
export function hrEstimateOvertimeSplit(input: HrOvertimeSplitInput): HrOvertimeSplit {
  const {
    checkIn,
    checkOut,
    otherDayRegularMinutes = 0,
    otherDayBreakMinutes = 0,
  } = input;
  const expectedMinutes = Math.max(0, input.expectedMinutes - Math.max(0, otherDayRegularMinutes));
  const breakMinutes = Math.max(0, (input.breakMinutes ?? 0) - Math.max(0, otherDayBreakMinutes));
  if (!checkIn || !checkOut || checkOut <= checkIn) return EMPTY_SPLIT;

  const startIso = toUtcIsoStringFromCompanyLocal(checkIn);
  const endIso = toUtcIsoStringFromCompanyLocal(checkOut);
  const startMs = new Date(startIso).getTime();
  const endMs = new Date(endIso).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return EMPTY_SPLIT;

  const workedMinutes = Math.round((endMs - startMs) / 60_000);
  const checkInMinute = minuteOfDay(checkIn);
  const shiftStart = minuteOfDay(input.shiftStartTime);
  const shiftEnd = minuteOfDay(input.shiftEndTime);

  let windowStart: number;
  let windowEnd: number;
  const knownWindow = checkInMinute != null && shiftStart != null && shiftEnd != null;
  if (knownWindow) {
    windowStart = shiftStart! - checkInMinute!;
    windowEnd = shiftEnd! - checkInMinute!;
    if (windowEnd <= windowStart) windowEnd += MINUTES_PER_DAY;
    if (windowEnd <= 0) {
      windowStart += MINUTES_PER_DAY;
      windowEnd += MINUTES_PER_DAY;
    }
  } else {
    // Without a known window the first expected hours count as regular and the tail as overtime.
    windowStart = 0;
    windowEnd = expectedMinutes + breakMinutes;
  }

  if (!knownWindow && windowEnd <= 0) {
    const overtimeMinutes = Math.max(workedMinutes - breakMinutes, 0);
    return {
      workedMinutes,
      breakMinutes: Math.min(breakMinutes, workedMinutes),
      regularMinutes: 0,
      overtimeMinutes,
      segments: overtimeMinutes > 0
        ? [segment(HR_SEGMENT_OVERTIME_UNSCHEDULED, true, startMs, 0, workedMinutes, overtimeMinutes)]
        : [],
    };
  }

  const regularStart = clamp(windowStart, 0, workedMinutes);
  const regularEnd = clamp(windowEnd, 0, workedMinutes);
  const beforeSpan = regularStart;
  const regularSpan = Math.max(regularEnd - regularStart, 0);
  const afterSpan = Math.max(workedMinutes - regularEnd, 0);

  // Breaks are scheduled inside the shift, so they come out of regular time before overtime.
  // Outside-shift-only punches must not consume the unpaid break budget.
  const breakFromRegular = Math.min(breakMinutes, regularSpan);
  let breakLeft = breakMinutes - breakFromRegular;
  const spillOtBreak = regularSpan > 0;
  const breakFromBefore = spillOtBreak ? Math.min(breakLeft, beforeSpan) : 0;
  breakLeft -= breakFromBefore;
  const breakFromAfter = spillOtBreak ? Math.min(breakLeft, afterSpan) : 0;

  let beforeMinutes = beforeSpan - breakFromBefore;
  let regularMinutes = regularSpan - breakFromRegular;
  let afterMinutes = afterSpan - breakFromAfter;

  // Cap payable OT to excess over remaining expected (matches backend day allocation).
  const productive = regularMinutes + beforeMinutes + afterMinutes;
  let toConvert = Math.max(
    0,
    beforeMinutes + afterMinutes - Math.max(0, productive - Math.max(expectedMinutes, 0)),
  );
  if (toConvert > 0 && beforeMinutes > 0) {
    const convert = Math.min(toConvert, beforeMinutes);
    beforeMinutes -= convert;
    regularMinutes += convert;
    toConvert -= convert;
  }
  if (toConvert > 0 && afterMinutes > 0) {
    const convert = Math.min(toConvert, afterMinutes);
    afterMinutes -= convert;
    regularMinutes += convert;
  }

  // Promote latest regular minutes when window-outside OT is short of the excess.
  let needOt = Math.max(0, productive - Math.max(expectedMinutes, 0)) - (beforeMinutes + afterMinutes);
  if (needOt > 0 && regularMinutes > 0) {
    const convert = Math.min(needOt, regularMinutes);
    regularMinutes -= convert;
    afterMinutes += convert;
    needOt -= convert;
  }

  const segments: HrOvertimeSplitSegment[] = [];
  const beforeEnd = beforeMinutes;
  const afterStart = workedMinutes - afterMinutes;
  if (beforeMinutes > 0) {
    segments.push(segment(HR_SEGMENT_OVERTIME_BEFORE, true, startMs, 0, beforeEnd, beforeMinutes));
  }
  if (regularMinutes > 0) {
    segments.push(segment(HR_SEGMENT_REGULAR, false, startMs, beforeEnd, afterStart, regularMinutes));
  }
  if (afterMinutes > 0) {
    segments.push(segment(HR_SEGMENT_OVERTIME_AFTER, true, startMs, afterStart, workedMinutes, afterMinutes));
  }

  return {
    workedMinutes,
    breakMinutes: breakFromRegular + breakFromBefore + breakFromAfter,
    regularMinutes,
    overtimeMinutes: beforeMinutes + afterMinutes,
    segments,
  };
}

/** Overtime minutes only, for the guard that requires an overtime type before saving. */
export function hrEstimateOvertimeMinutes(input: HrOvertimeSplitInput): number {
  return hrEstimateOvertimeSplit(input).overtimeMinutes;
}

function segment(
  type: string,
  overtime: boolean,
  startMs: number,
  startOffset: number,
  endOffset: number,
  minutes: number,
): HrOvertimeSplitSegment {
  return {
    type,
    overtime,
    start: new Date(startMs + startOffset * 60_000).toISOString(),
    end: new Date(startMs + endOffset * 60_000).toISOString(),
    minutes,
  };
}
